import React, { useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, Trash2, ArrowLeftRight } from "lucide-react";

/** Iter163: Rückzahl-Assistent Dialog.
 *
 *  Wird geöffnet wenn der User auf einen verbuchten Beleg "Rückverbuchen" klickt.
 *  Ruft /receipts/{id}/unbook-preview auf und zeigt Warnungen + Modus-Auswahl:
 *    - "delete"  → Journal-Eintrag hart löschen (verliert Historie)
 *    - "storno"  → Storno-JE erzeugen, Original bleibt archiviert (GeBüV-konform)
 */
export function UnbookAssistantDialog({ receipt, open, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("delete");
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (!open || !receipt?._id) return;
    setLoading(true);
    setPreview(null);
    apiClient
      .get(`/receipts/${receipt._id}/unbook-preview`)
      .then((r) => {
        setPreview(r.data);
        const rec = r.data.options?.find((o) => o.recommended);
        setMode(rec?.mode || "delete");
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Preview fehlgeschlagen");
      })
      .finally(() => setLoading(false));
  }, [open, receipt?._id]);

  const toInbox = !!receipt?._toInbox;

  const doUnbook = async () => {
    if (!receipt?._id) return;
    setExecuting(true);
    try {
      if (toInbox) {
        await apiClient.post(`/receipts/${receipt._id}/to-inbox`, { unbook_mode: mode });
        toast.success(
          mode === "storno"
            ? "Storno angelegt – Beleg ist wieder im Posteingang."
            : "Buchung aufgehoben – Beleg ist wieder im Posteingang.",
          { action: { label: "Zum Posteingang", onClick: () => (window.location.href = "/inbox") } }
        );
      } else {
        await apiClient.post(`/receipts/${receipt._id}/unbook`, { mode });
        toast.success(
          mode === "storno"
            ? "Storno-Eintrag angelegt. Journal-Historie bleibt erhalten."
            : "Beleg zurückgebucht. Journal-Eintrag gelöscht."
        );
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rückverbuchen fehlgeschlagen");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="unbook-assistant-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-amber-600" />
            {toInbox ? "Rückbuchen & in den Posteingang" : "Rückverbuchen-Assistent"}
          </DialogTitle>
        </DialogHeader>
        {toInbox && (
          <p className="text-xs text-slate-600 -mt-2" data-testid="unbook-to-inbox-hint">
            Die Buchung wird rückgängig gemacht und der Beleg landet wieder im Posteingang, wo du Datum, Belegart und Konto neu zuordnen kannst.
          </p>
        )}

        {loading && (
          <div className="p-6 text-center text-sm text-slate-500">
            Lade Vorschau…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-4">
            {preview.warnings?.length > 0 && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2"
                data-testid="unbook-warnings"
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  {preview.warnings.map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              </div>
            )}

            {preview.journal_entries?.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-medium text-slate-600 mb-2">
                  Betroffene Journal-Einträge:
                </div>
                <ul className="space-y-1">
                  {preview.journal_entries.map((je) => (
                    <li
                      key={je.id}
                      className="text-xs text-slate-700 flex items-center justify-between"
                    >
                      <span>
                        {formatDate(je.date)} · {je.description} · {je.line_count} Zeile(n)
                      </span>
                      <span className="font-mono">{je.total?.toFixed(2)} CHF</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.has_bank_match && (
              <Badge className="bg-sky-100 text-sky-700 border-sky-200 border">
                🔗 Mit Bank-Match verknüpft
              </Badge>
            )}

            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">
                Rückverbuchen-Modus:
              </div>
              <div className="space-y-2">
                {preview.options?.map((opt) => (
                  <label
                    key={opt.mode}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      mode === opt.mode
                        ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`unbook-mode-${opt.mode}`}
                  >
                    <input
                      type="radio"
                      name="unbook-mode"
                      checked={mode === opt.mode}
                      onChange={() => setMode(opt.mode)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {opt.mode === "delete" ? (
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        ) : (
                          <ArrowLeftRight className="h-4 w-4 text-emerald-600" />
                        )}
                        <span className="font-medium text-sm">{opt.label}</span>
                        {opt.recommended && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]">
                            Empfohlen
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-1">
                        {opt.mode === "delete"
                          ? "Der Journal-Eintrag wird gelöscht. Schnell, aber die Buchungshistorie geht verloren."
                          : "Ein Storno-Eintrag wird angelegt, der Original bleibt archiviert. Volle Historie, GeBüV-konform."}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="unbook-cancel"
              >
                Abbrechen
              </Button>
              <Button
                onClick={doUnbook}
                disabled={executing || !preview.unbookable}
                className={
                  mode === "storno"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-amber-600 hover:bg-amber-700 text-white"
                }
                data-testid="unbook-confirm"
              >
                {executing
                  ? "Führe aus…"
                  : toInbox
                  ? (mode === "storno" ? "Storno & in Posteingang" : "Journal löschen & in Posteingang")
                  : mode === "storno"
                  ? "Storno anlegen"
                  : "Journal löschen"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UnbookAssistantDialog;
